import {
    DropdownListContainer,
    DropdownListContainerLI,
    DropdownListContainerUL,
    DropdownListLoader,
} from "shared/components/inputs/dropdown/Dropdown.styles";
import React, { Fragment, useEffect, useRef } from "react";

import DropdownListVariant from "shared/components/inputs/dropdown-multi-select/DropdownListVariant";
import PropTypes from "prop-types";
import TextInput from "shared/components/inputs/input/TextInput";
import {
    StyledDropdownListContainerLIWrapper,
    DropdownListContainerLIStyled,
    StyledUL,
} from "./DropdownMultiSelectList.styles";

const DropdownMultiSelectChildren = ({
    isVariantToggle,
    itemChildren,
    getItemProps,
    selectItem,
    // highlightedIndex,
}) => {
    return (
        <StyledUL>
                {itemChildren?.map((item, index) => (
                    <DropdownListContainerLIStyled
                        // isTraversed={highlightedIndex === index ? true : false}
                        key={item.childOptionIndex ?? index}
                        {...getItemProps({
                            key: item.childOptionIndex ?? index,
                            item,
                            index: item.childOptionIndex ?? index,
                        })}
                    >
                        <DropdownListVariant
                            isVariantToggle={isVariantToggle}
                            id={`downshift-0-item-${
                                item.childOptionIndex ?? index
                            }id`}
                            htmlFor={`downshift-0-item-${
                                item.childOptionIndex ?? index
                            }id`}
                            checked={item.check}
                            onChange={() => {
                                selectItem(item);
                            }}
                            label={item.label}
                        />
                    </DropdownListContainerLIStyled>
                ))}
            </StyledUL>
    );
};

const DropdownMultiSelectList = ({
    isVariantToggle,
    disabled = false,
    isOpen,
    optionsList,
    optionsAreLoading = false,
    filterValue,
    setFilterValue,
    internalFilterUIShown,
    getMenuProps,
    getInputProps,
    getDropdownProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    isBorder = true,
    position = "absolute",
}) => {
    const filterInputRef = useRef();
    const listScrollerRef = useRef();
    const pauseBlurEvent = useRef(false);

    // focuses list's filter field on open. that focus causes
    // a blur on the menu, so pause Downshift's built-in blur
    // action to keep the menu from closing
    useEffect(() => {
        if (isOpen && internalFilterUIShown) {
            setTimeout(() => {
                pauseBlurEvent.current = true;
                filterInputRef.current?.focus();
                pauseBlurEvent.current = false;
            });
        }
    }, [isOpen, internalFilterUIShown]);

    useEffect(() => {
        listScrollerRef.current.scrollTop = 0;
    }, [filterValue]);

    return (
        <DropdownListContainer
                hidden={
                    !(
                        isOpen &&
                        (filterValue.length > 0 || optionsList.length > 0)
                    )
                }
                className="dropdown-list-container"
                position={position}
            >
                {optionsAreLoading && <DropdownListLoader />}

                <DropdownListContainerUL
                    hidden={optionsAreLoading}
                    {...getMenuProps({
                        onBlur: (event) => {
                            if (pauseBlurEvent.current) {
                                event.nativeEvent.preventDownshiftDefault = true;
                            }
                        },
                    })}
                    className="dropdown-list-container-ul"
                >
                    <div
                        {...getInputProps(
                            getDropdownProps({
                                preventKeyAction: isOpen,
                                disabled: disabled,
                                placeholder: "Search...",
                            })
                        )}
                        style={{ display: "none" }}
                    ></div>
                    {internalFilterUIShown && (
                        <TextInput
                            type="search"
                            placeholder="Search..."
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                            css="margin: -1px -1px 0 -1px; width: calc(100% + 2px);"
                            ref={filterInputRef}
                            onKeyDown={(e) => {
                                if (e.code === "Space") {
                                    e.stopPropagation();
                                }
                            }}
                            showClearIcon={true}
                        />
                    )}

                    <StyledDropdownListContainerLIWrapper ref={listScrollerRef}>
                        {optionsList?.length ? (
                            optionsList.map((item, index) => (
                                <Fragment key={item.id}>
                                    <DropdownListContainerLIStyled
                                        isBorder={isBorder}
                                    >
                                        <DropdownListVariant
                                            isVariantToggle={isVariantToggle}
                                            isTraversed={highlightedIndex === index}
                                            key={`${item.label}${index}`}
                                            {...getItemProps({
                                                key: item.id,
                                                item,
                                                index: item.parentOptionIndex,
                                            })}
                                            indeterminate={item?.indeterminate}
                                            checked={item.check}
                                            label={item.label}
                                        />
                                    </DropdownListContainerLIStyled>
                                    <DropdownMultiSelectChildren
                                        // highlightedIndex={highlightedIndex}
                                        isVariantToggle={isVariantToggle}
                                        selectItem={selectItem}
                                        itemChildren={optionsList[
                                            index
                                        ].children?.filter(
                                            (f) => f.parentId === item.id
                                        )}
                                        getItemProps={getItemProps}
                                    />
                                </Fragment>
                            ))
                        ) : (
                            <DropdownListContainerLI>
                                No matches found
                            </DropdownListContainerLI>
                        )}
                    </StyledDropdownListContainerLIWrapper>
                </DropdownListContainerUL>
            </DropdownListContainer>
    );
};

DropdownMultiSelectChildren.propTypes = {
    isVariantToggle: PropTypes.bool,
    itemChildren: PropTypes.array,
    getItemProps: PropTypes.func,
    selectItem: PropTypes.func,
};

DropdownMultiSelectList.propTypes = {
    isVariantToggle: PropTypes.bool,
    disabled: PropTypes.bool,
    isOpen: PropTypes.bool,
    optionsList: PropTypes.array,
    optionsAreLoading: PropTypes.bool,
    filterValue: PropTypes.string,
    setFilterValue: PropTypes.func,
    internalFilterUIShown: PropTypes.bool,
    getMenuProps: PropTypes.func,
    getInputProps: PropTypes.func,
    getDropdownProps: PropTypes.func,
    highlightedIndex: PropTypes.number,
    getItemProps: PropTypes.func,
    selectItem: PropTypes.func,
    isBorder: PropTypes.bool,
    position: PropTypes.string,
};

export default DropdownMultiSelectList;
